import type { BlogLens, BlogPlatform, BlogIndustry } from "./blogPosts";
import {
  LENS_OPTIONS,
  PLATFORM_OPTIONS,
  INDUSTRY_OPTIONS,
  labelForLens,
  labelForPlatform,
  labelForIndustry,
} from "./blogPosts";

export type CaseLens = BlogLens;
export type CasePlatform = BlogPlatform;
export type CaseIndustry = BlogIndustry;

export const CASE_LENS_OPTIONS = LENS_OPTIONS;
export const CASE_PLATFORM_OPTIONS = PLATFORM_OPTIONS;
export const CASE_INDUSTRY_OPTIONS = INDUSTRY_OPTIONS;
export const labelForCaseLens = labelForLens;
export const labelForCasePlatform = labelForPlatform;
export const labelForCaseIndustry = labelForIndustry;

export interface CaseStudyTimelineItem {
  phase: string;
  title: string;
  body: string;
}

export interface CaseStudyFourLayer {
  experience: string;
  data: string;
  activation: string;
  optimization: string;
}

export interface CaseStudy {
  slug: string;
  client: string;
  /** Public-facing label e.g. "Engagement record · 2025" */
  label: string;
  /** One-line headline summarising the engagement outcome. */
  headline: string;
  /** 1–2 sentence excerpt for the index card. */
  excerpt: string;
  /** Engagement window. */
  window: string;
  /** Mode we plugged in as (assessment / blueprint / build). */
  mode: "Assessment" | "Blueprint" | "Build" | "Build · Run";
  /** Up to 3 outcome metrics for the card. */
  outcomes: { value: string; label: string }[];
  lenses?: CaseLens[];
  platforms?: CasePlatform[];
  industries?: CaseIndustry[];

  // ── Rich detail-page fields ──────────────────────────────────────────────
  /** 2–4 short paragraphs describing the situation we walked into. */
  situation?: string[];
  /** How the work mapped onto the four-layer framework. Optional per layer. */
  fourLayer?: Partial<CaseStudyFourLayer>;
  /** Engagement timeline broken into named phases. */
  timeline?: CaseStudyTimelineItem[];
  /** Stack rendered as label/value rows. */
  stack?: { label: string; value: string }[];
  /** Pod composition rendered as a list. */
  team?: string[];
  /** A pull-quote from the partner / sponsor. */
  partnerQuote?: { quote: string; attribution: string };
  /** Two suggested next-read internal links surfaced at the foot of the record. */
  nextReads?: { label: string; to: string; eyebrow: string }[];
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "regional-hospital-portal-replatform",
    client: "Regional hospital network",
    label: "Engagement record · 2025",
    headline:
      "Patient-portal replatform on Drupal + AWS, with an AI-assisted content migration pod.",
    excerpt:
      "Twelve sub-sites, 18,000 pages, four CMSes - consolidated onto a single Drupal 10 platform on AWS. Migration pod paired engineers with an AI editor that flagged duplicate, stale, and PHI-leaking content before publish.",
    window: "8 months · 2025",
    mode: "Build · Run",
    outcomes: [
      { value: "94%", label: "auto-migrated pages" },
      { value: "2.1s", label: "p75 page load" },
      { value: "0", label: "PHI incidents post-launch" },
    ],
    lenses: ["engineering", "design"],
    platforms: ["drupal", "aws"],
    industries: ["healthcare"],
    situation: [
      "Twelve regional sub-sites across four CMSes - Drupal 7, two flavours of WordPress, and a homegrown PHP stack a previous vendor had abandoned. Eighteen thousand pages of clinical, marketing, and patient-portal content. A compliance team that had stopped trusting any of it.",
      "The hospital's digital director had a board commitment to ship a unified portal in the year. Two prior vendors had pitched lift-and-shift migrations and walked. The internal team knew the real risk: PHI leaking into pages it shouldn't, and a migration that would silently strand half the clinical content.",
    ],
    fourLayer: {
      experience:
        "One Drupal 10 design system across patient-facing, clinician-facing, and marketing surfaces. Authoring interface restructured around clinical service lines, not org chart. WCAG 2.2 AA on launch.",
      data:
        "Canonical content model that finally distinguished clinical pages from marketing pages from portal pages. A single source of truth for service-line metadata that the CRM and the call-center team now read from too.",
      activation:
        "Patient-portal sign-up, appointment-request, and bill-pay flows rebuilt with measurement on every step. Conversion lifted on three of four flows in the first quarter.",
      optimization:
        "AI-assisted content audit that runs weekly post-launch - flags pages drifting on accuracy, accessibility, or PHI exposure before they go stale. Owned by the compliance team, not by us.",
    },
    timeline: [
      {
        phase: "Week 0–4",
        title: "Discovery + content audit",
        body: "Crawled all 18k pages. Built the content inventory, the PHI-exposure heat map, and the migration scoring rubric the compliance team signed off on.",
      },
      {
        phase: "Week 5–10",
        title: "Design system + content model",
        body: "Co-designed the component library with the brand team. Locked the content model with clinical and marketing leads in the same room - a first for them.",
      },
      {
        phase: "Week 11–24",
        title: "AI-assisted migration pod",
        body: "Two engineers + an AI editor ran the migration in waves. Editor flagged duplicates, stale pages, and PHI risk; humans cleared every flag before publish. 94% auto-migrated; 6% rewritten.",
      },
      {
        phase: "Week 25–32",
        title: "Launch + hand-off",
        body: "Phased cutover by service line. Zero PHI incidents in the 90 days post-launch. Stewardship handed to internal team on a documented runbook + weekly content audit.",
      },
    ],
    stack: [
      { label: "CMS", value: "Drupal 10" },
      { label: "Hosting", value: "AWS · multi-AZ" },
      { label: "Search", value: "OpenSearch" },
      { label: "Edge", value: "CloudFront + WAF" },
      { label: "AI tooling", value: "Custom content-audit agent · in-house" },
    ],
    team: [
      "1× Engagement Director",
      "1× Tech Architect (Drupal)",
      "2× Senior Engineers",
      "1× UX + design system lead",
      "1× Content strategist",
      "1× AI/ML engineer (audit agent)",
      "1× QA lead",
    ],
    partnerQuote: {
      quote:
        "We'd been quoted lift-and-shift twice. What we actually needed was someone who'd read every page with us and tell us which ones shouldn't exist. The AI did the reading; their team did the judgment. Both mattered.",
      attribution: "Director of Digital · Regional hospital network · US",
    },
    nextReads: [
      {
        eyebrow: "Approach",
        label: "How we plug in (Assessment · Blueprint · Build)",
        to: "/approach",
      },
      {
        eyebrow: "Dispatch",
        label: "Trust is earned, not assumed",
        to: "/blog/trust-is-earned-not-assumed",
      },
    ],
  },
  {
    slug: "land-grant-university-acquia-blueprint",
    client: "Land-grant university system",
    label: "Engagement record · 2025",
    headline:
      "Acquia Cloud Site Factory blueprint covering 47 college and department sites.",
    excerpt:
      "Twelve-week blueprint engagement: governance model, design system, and a reference build their internal team executes against. We stayed on as the technology partner running the first three site rollouts alongside them.",
    window: "12 weeks · 2025",
    mode: "Blueprint",
    outcomes: [
      { value: "47", label: "sites in scope" },
      { value: "1", label: "shared design system" },
      { value: "3", label: "reference rollouts shipped" },
    ],
    lenses: ["engineering", "design"],
    platforms: ["acquia", "drupal"],
    industries: ["higher-ed"],
    situation: [
      "Forty-seven college, department, and program sites across four CMSes and a long tail of one-off WordPress installs. A digital strategy team of six trying to enforce brand and accessibility on an estate of two hundred contributors.",
      "The provost had asked for one university, one stack, one accessible front door. The internal team had picked Acquia Cloud Site Factory; what they didn't have was the operating model that would let 47 sites ship without 47 different roadmaps.",
    ],
    fourLayer: {
      experience:
        "Shared design system co-owned by central marketing and the colleges. Component library with a documented contribution path for departments who wanted bespoke work.",
      data:
        "Canonical program catalog, faculty directory, and event model. Departments stop maintaining their own copies; the catalog renders into every site that needs it.",
      activation:
        "Inquiry, application-start, and event-registration flows standardized across the federation. One reporting model the recruitment marketing team can finally read.",
      optimization:
        "Quarterly governance review process baked into the blueprint. Drift surfaced as a report, not as a complaint from the brand team.",
    },
    timeline: [
      {
        phase: "Week 1–2",
        title: "Read the federation",
        body: "Audited 47 sites end-to-end - content model, brand, accessibility, performance. Mapped the political model, not just the technical one.",
      },
      {
        phase: "Week 3–6",
        title: "Co-author the blueprint",
        body: "Architecture, content model, design system, governance model, integration map (SIS, CRM, LMS). Reviewed with provost, IT, marketing, and a faculty advisory in the same room.",
      },
      {
        phase: "Week 7–10",
        title: "Reference rollouts",
        body: "Three sites built end-to-end as the canonical reference: a flagship college, a department, and a niche program. Internal team paired through every PR.",
      },
      {
        phase: "Week 11–12",
        title: "Hand-off + roadmap",
        body: "Documented playbook handed to internal team. We stayed on as the technology partner for the first six months of broader rollout - not as the executor, as the safety net.",
      },
    ],
    stack: [
      { label: "Hosting", value: "Acquia Cloud Site Factory" },
      { label: "CMS", value: "Drupal 10" },
      { label: "Design system", value: "Storybook + custom Twig components" },
      { label: "Integrations", value: "Banner SIS · Salesforce · Canvas" },
      { label: "Accessibility", value: "Automated WCAG 2.2 AA on every PR" },
    ],
    team: [
      "1× Engagement Director",
      "1× Acquia / Drupal architect",
      "1× Senior engineer",
      "1× Design system lead",
      "1× Content + governance strategist",
      "1× Marketing-ops engineer (CRM integration)",
    ],
    partnerQuote: {
      quote:
        "Every vendor pitched us a CMS. Axelerant pitched us an operating model. Six months in, our 47 department sites finally look like one university - and the brand team isn't policing it manually anymore.",
      attribution: "Director of Digital Strategy · Land-grant university · US",
    },
    nextReads: [
      {
        eyebrow: "Industry",
        label: "For higher-ed digital teams",
        to: "/industries/higher-ed",
      },
      {
        eyebrow: "Approach",
        label: "How we plug in (Assessment · Blueprint · Build)",
        to: "/approach",
      },
    ],
  },
  {
    slug: "global-non-profit-hubspot-marketing-ops",
    client: "Global humanitarian non-profit",
    label: "Engagement record · 2024",
    headline:
      "HubSpot marketing-ops pod that doubled qualified donor inquiries in two quarters.",
    excerpt:
      "An embedded marketing pod - strategist, RevOps engineer, two-agent automation - rebuilt their HubSpot lifecycle, donor segmentation, and reporting on a quarterly cadence.",
    window: "6 months · 2024",
    mode: "Build · Run",
    outcomes: [
      { value: "2.1×", label: "qualified inquiries" },
      { value: "−38%", label: "manual ops hours" },
      { value: "11", label: "automated journeys live" },
    ],
    lenses: ["marketing"],
    platforms: ["hubspot"],
    industries: ["non-profit"],
    situation: [
      "A global humanitarian non-profit raising mid-six-figure gifts from individual donors. Their HubSpot instance had been bolted on over five years by three different agencies. Lifecycle stages were inconsistent across regions; donor segmentation lived in a senior fundraiser's head.",
      "Inquiry volume was strong; conversion to a first conversation was not. The CMO needed the funnel rebuilt without pausing campaigns, and the data clean enough that quarterly board reports stopped requiring a week of manual reconciliation.",
    ],
    fourLayer: {
      experience:
        "Donor-facing forms, landing pages, and email templates rebuilt against a refreshed brand system. Region-specific without the duplication.",
      data:
        "Lifecycle model rationalized across regions. Donor segments defined in HubSpot, not in a fundraiser's spreadsheet. CRM hygiene jobs running nightly.",
      activation:
        "Eleven lifecycle automations live: welcome, re-engagement, gift-acknowledgement, mid-level cultivation, lapsed-donor, event follow-up. Two agents handling routing and personalization under human review.",
      optimization:
        "Quarterly board dashboard that builds itself. The fundraising team stopped exporting to spreadsheets to make decisions.",
    },
    timeline: [
      {
        phase: "Month 1",
        title: "Audit + lifecycle redesign",
        body: "Read the HubSpot instance end-to-end. Documented lifecycle, segmentation, and the gaps. Co-designed the new lifecycle model with regional fundraising leads.",
      },
      {
        phase: "Month 2–3",
        title: "Foundations + first three journeys",
        body: "Rebuilt the property model, lifecycle stages, and segmentation. Shipped welcome, re-engagement, and gift-acknowledgement. Shut down 14 redundant workflows.",
      },
      {
        phase: "Month 4–5",
        title: "Agent-assisted journeys",
        body: "Mid-level cultivation, lapsed-donor, and event follow-up - two AI agents handling personalization drafts and routing under human review. All copy approved before send.",
      },
      {
        phase: "Month 6",
        title: "Hand-off + run cadence",
        body: "Quarterly board dashboard live. Pod transitions to a fractional run cadence - one strategist + one engineer, two days a month. Inquiry volume +2.1× quarter on quarter.",
      },
    ],
    stack: [
      { label: "Marketing automation", value: "HubSpot Enterprise" },
      { label: "CRM", value: "HubSpot CRM (donor)" },
      { label: "Reporting", value: "HubSpot dashboards + Looker" },
      { label: "Agents", value: "Routing + personalization pods (custom)" },
      { label: "Forms / pages", value: "HubSpot CMS Hub" },
    ],
    team: [
      "1× Marketing Engagement Director",
      "1× RevOps engineer (HubSpot Solutions Architect)",
      "1× Lifecycle strategist",
      "1× Copy + brand lead",
      "2× Agent engineers (routing + personalization)",
    ],
    partnerQuote: {
      quote:
        "We didn't need a new CRM. We needed someone to make the one we had behave. Six months later, the board report writes itself and the fundraising team is having more first conversations than ever.",
      attribution: "CMO · Global humanitarian non-profit · UK",
    },
    nextReads: [
      {
        eyebrow: "Dispatch",
        label: "What we learned running 5 agent pods on a 6-platform engagement",
        to: "/blog/what-we-learned-running-5-agent-pods",
      },
      {
        eyebrow: "Approach",
        label: "How we plug in (Assessment · Blueprint · Build)",
        to: "/approach",
      },
    ],
  },
  {
    slug: "specialty-retail-aws-assessment",
    client: "Specialty retail chain",
    label: "Engagement record · 2025",
    headline:
      "AWS delivery-readiness assessment ahead of a multi-region commerce migration.",
    excerpt:
      "Six-week assessment: production telemetry, deployment maturity, AI-tooling fit. Output was a 40-page execution plan their internal team is now running - we returned to lead the search-experience rebuild.",
    window: "6 weeks · 2025",
    mode: "Assessment",
    outcomes: [
      { value: "40pp", label: "execution plan delivered" },
      { value: "3", label: "regions readied" },
      { value: "1", label: "follow-on build engaged" },
    ],
    lenses: ["engineering", "marketing"],
    platforms: ["aws"],
    industries: ["retail"],
    situation: [
      "A specialty retailer with a single-region AWS footprint preparing to expand into three regions over the next year. The CTO wanted an honest read on whether the existing platform - and the existing team - could carry the migration without the wheels coming off.",
      "Two consulting firms had pitched build-led engagements. The CTO wanted the assessment first: tell me what's good, tell me what isn't, and tell me what to fix in what order before anyone touches infrastructure.",
    ],
    fourLayer: {
      experience:
        "Storefront, checkout, and search experience reviewed across desktop and mobile. Performance budgets vs. actual telemetry mapped page by page.",
      data:
        "Product, inventory, and order data flow audited end-to-end. Region-readiness gaps documented (latency, eventual consistency boundaries, currency / tax model).",
      activation:
        "Checkout funnel and search conversion instrumented properly for the first time. Quick-win opportunities flagged separately from migration-dependent work.",
      optimization:
        "Deployment maturity, observability stack, and on-call posture scored against a delivery-readiness rubric. Improvement plan ordered by ROI, not by team preference.",
    },
    timeline: [
      {
        phase: "Week 1",
        title: "Intake + scope-lock",
        body: "Read the architecture docs, the on-call channel, the last six months of incidents. Locked the assessment scope with the CTO + VP Engineering.",
      },
      {
        phase: "Week 2–4",
        title: "Deep read",
        body: "Telemetry review, deployment maturity scoring, AI-tooling fit assessment. Paired with their senior engineers for two days each week.",
      },
      {
        phase: "Week 5",
        title: "Plan authoring",
        body: "Forty-page execution plan: prioritized improvement backlog, region-readiness sequence, observability + deployment uplift, AI-tooling pilots.",
      },
      {
        phase: "Week 6",
        title: "Readout + decision",
        body: "Readout to CTO + exec team. Plan handed over. We returned three months later to lead the search-experience rebuild as a build engagement.",
      },
    ],
    stack: [
      { label: "Cloud", value: "AWS · single-region today" },
      { label: "Compute", value: "ECS Fargate + Lambda" },
      { label: "Data", value: "RDS Aurora · DynamoDB · OpenSearch" },
      { label: "Observability", value: "Datadog + custom SLO model" },
      { label: "Commerce", value: "Headless · custom" },
    ],
    team: [
      "1× Engagement Director",
      "1× Principal AWS architect",
      "1× SRE / observability lead",
      "1× Commerce experience lead",
      "1× AI tooling assessor",
    ],
    partnerQuote: {
      quote:
        "Two firms wanted to start building. They wanted to read first. The forty-page plan saved us from at least two of the choices we were about to make. That's the assessment I needed.",
      attribution: "CTO · Specialty retail chain · US",
    },
    nextReads: [
      {
        eyebrow: "Approach",
        label: "How we plug in (Assessment · Blueprint · Build)",
        to: "/approach",
      },
      {
        eyebrow: "Dispatch",
        label: "The delivery intelligence gap",
        to: "/blog/the-delivery-intelligence-gap",
      },
    ],
  },
  {
    slug: "private-college-drupal-design-system",
    client: "Private liberal-arts college",
    label: "Engagement record · 2024",
    headline:
      "Brand-led Drupal design system that cut new-page build time by two-thirds.",
    excerpt:
      "Design and engineering paired on a component library co-owned by their marketing and IT teams. AI-assisted accessibility audit ran on every PR - kept WCAG 2.2 AA from day one.",
    window: "5 months · 2024",
    mode: "Build",
    outcomes: [
      { value: "−66%", label: "page build time" },
      { value: "100%", label: "WCAG 2.2 AA on launch" },
      { value: "32", label: "shared components" },
    ],
    lenses: ["design", "engineering"],
    platforms: ["drupal"],
    industries: ["higher-ed"],
    situation: [
      "A private liberal-arts college with a beautiful brand and a website that didn't carry it. Marketing was building landing pages in a page builder; IT was holding the line on a Drupal core that hadn't been seriously refactored in five years.",
      "The new VP of Marketing wanted a design system both teams could ship from. The CIO wanted accessibility as a deployment gate, not an audit fire-drill. Both wanted the new admissions cycle to ship on the new system.",
    ],
    fourLayer: {
      experience:
        "Thirty-two shared components covering 90% of new-page need. Authoring UX rebuilt around marketing's actual workflow - not Drupal defaults.",
      data:
        "Program, faculty, and event content models normalized. Marketing finally stopped re-typing the program list into landing pages.",
      activation:
        "Inquiry and visit-booking flows rebuilt with measurement on every step. Conversion lifted on both within the admissions cycle.",
      optimization:
        "AI-assisted accessibility check on every PR. Performance budget enforced at deploy time. Drift report runs weekly, owned by the IT team.",
    },
    timeline: [
      {
        phase: "Month 1",
        title: "Discovery + design audit",
        body: "Audited the live site, the brand, and the page-builder estate. Co-designed the component library taxonomy with marketing + IT in the same room.",
      },
      {
        phase: "Month 2–3",
        title: "Component library build",
        body: "Built 32 components in Storybook + Drupal Twig. Accessibility-first by default. Marketing reviewed every component before it shipped.",
      },
      {
        phase: "Month 4",
        title: "Reference pages + migration",
        body: "Migrated the admissions, programs, and visit pages onto the new system as the canonical references. Internal team paired through every page.",
      },
      {
        phase: "Month 5",
        title: "Hand-off + admissions launch",
        body: "Admissions cycle launched on the new system. New-page build time down 66%. WCAG 2.2 AA enforced at PR. Stewardship handed to internal team.",
      },
    ],
    stack: [
      { label: "CMS", value: "Drupal 10" },
      { label: "Design system", value: "Storybook + Twig components" },
      { label: "Hosting", value: "Acquia" },
      { label: "Accessibility", value: "Automated WCAG 2.2 AA on every PR" },
      { label: "Performance", value: "Deploy-gate performance budget" },
    ],
    team: [
      "1× Engagement Director",
      "1× Design system lead",
      "1× Senior Drupal engineer",
      "1× Frontend engineer",
      "1× Accessibility specialist",
      "1× Marketing-pod liaison",
    ],
    partnerQuote: {
      quote:
        "The design system isn't the win. The win is that marketing and IT are now shipping from the same library. Two years of arguing in the rear-view mirror in five months.",
      attribution: "VP of Marketing · Private liberal-arts college · US",
    },
    nextReads: [
      {
        eyebrow: "Industry",
        label: "For higher-ed digital teams",
        to: "/industries/higher-ed",
      },
      {
        eyebrow: "Approach",
        label: "How we plug in (Assessment · Blueprint · Build)",
        to: "/approach",
      },
    ],
  },
];
